import { Text } from "@zapier/design-system";
import styles from "./Sidebar.module.css";
import { buildNavigationRoute, ServiceRoute } from "src/utils/routes";
import { HomepageMenuItem, SidebarMenu } from "./Components/SidebarMenu";
import { components } from "@zapier/api-clients/apis/templates-backend";
import { NavigationLink } from "src/types";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

type Category = components["schemas"]["Category"];
interface SidebarProps {
  categories: Category[];
  productsMenu?: ServiceRoute[];
  includeHomepage?: boolean;
  className?: string;
  isCustomPage: boolean;
  navigationApps: NavigationLink[];
}

/**
 * Users should see the top-most accordion in the menu as default open.
 * Ex: when there is a products, it's the open one. When there is an apps section, the categories one is the top one.
 */
export const Sidebar = ({
  categories,
  productsMenu,
  includeHomepage = false,
  className,
  isCustomPage,
  navigationApps,
  ...rest
}: SidebarProps) => {
  const pathname = usePathname();
  const t = useTranslations("Sidebar");

  const categoriesMenu = categories.map((category) =>
    buildNavigationRoute(
      category,
      pathname,
      isCustomPage ? "category" : undefined,
    ),
  );

  const navigationAppsMenu = navigationApps.map((app) =>
    buildNavigationRoute(app, pathname, "app"),
  );

  return (
    <div className={className} {...rest}>
      {includeHomepage ? (
        <HomepageMenuItem />
      ) : (
        <div className={styles.header}>
          <Text type="Body4">{t("browseBy")}</Text>
        </div>
      )}
      {productsMenu && productsMenu.length > 0 && (
        <SidebarMenu menu={productsMenu} defaultOpen title={t("products")} />
      )}
      <SidebarMenu
        menu={categoriesMenu}
        defaultOpen={isCustomPage}
        title={t("useCase")}
      />
      {navigationApps && navigationApps.length > 0 && (
        <SidebarMenu menu={navigationAppsMenu} title={t("apps")} />
      )}
    </div>
  );
};
